<template>
  <div>
    <div class="form-group">
      <label class="label-option">
        <span class="mr-2">{{ $i18nPresenter.t('URL or Embed code') }}</span>
        <span class="title-table-presentation-tooltip result-layout-tooltip">
          <v-popover
            trigger="hover"
            :delay="{ show: 100, hide: 500 }"
            placement="top"
            class="inline-block"
          >
            <i class="fas fa-question-circle cursor-pointer" />
            <template slot="popover">
              <p class="mb-2">{{ $i18nPresenter.t('We support many platforms—just drop the URL or embed code here!') }}</p>
              <a class="learn-more-link" :href="embedSlideKnowledgeBaseUrl" target="_blank">{{ $i18nPresenter.t('Learn more') }}</a>
              <aha-icon
                name="open-new-window"
                width="16"
                height="16"
                viewBox="-4 -4 32 32"
              />
            </template>
          </v-popover>

        </span>
      </label>
      <InputText
        :val="activeEmbedSlide?.attributes?.embedSrc"
        :key="embedInputKey"
        @interface="updateEmbedLink"
        :place-holder="'E.g.: https://youtube.com/watch?... or <iframe src=...'"
        spellcheck="false"
        :id-name="'embed-src-input'"
        class="mb-1"
        :index="''"
        :limit="2048"
      />
      <div v-if="embedSrcError" class="text-red-100 text-xs">
        {{ $i18nPresenter.t('That doesn\'t look like a valid link.') }}
        <a class="learn-more-link" :href="embedSlideKnowledgeBaseUrl" target="_blank">{{ $i18nPresenter.t('Learn how to get it') }}</a>
        <aha-icon
          name="open-new-window"
          width="16"
          height="16"
          viewBox="-4 -4 32 32"
        />
      </div>
    </div>
    <div class="form-group">
      <div class="setting-block">
        <div class="block-content">
          <div class="form-check form-check-inline">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="embed-check-show-iframe"
                v-aha-emit-action
                name="embed-slide-checkbox-show-iframe"
                :checked="showIframeOnParticipantScreen"
                @change="updateSettingShowIframeOnParticipantScreen"
              >
              <label
                class="custom-control-label pointer"
                for="embed-check-show-iframe"
              >
                <span class="mr-2">{{ $i18nPresenter.t('Also show on audience\'s smartphones') }}</span>
                <span class="title-table-presentation-tooltip">
                  <b-button
                    v-tooltip="showOnAudienceTooltip"
                    class="tooltip-btn"
                  />
                  <i class="fas fa-question-circle" />
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="form-group" :key="key">
      <span class="label-option">
        <label>{{ $i18nPresenter.t('Caption (optional)') }}</label>
      </span>
      <InputText
        :key="key"
        :val="activeEmbedSlide.title"
        @interface="updateSlideTitle"
        :place-holder="$i18nPresenter.t('Enter caption')"
        spellcheck="false"
        :id-name="'image-caption-slide-edit'"
        :index="''"
        :limit="80"
        :inputId="`slide-title-${activeEmbedSlide.id}`"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { sanitizeEmbedSrc } from '@/business-logic/embed-slide'
import Util from '@/util'
import Socket from '@/socket'
import Config from '@/config'
import InputText from '@/components/presentations/InputText.vue'

export default {
  name: 'EmbedConfig',
  components: {
    InputText,
  },
  data() {
    return {
      key: null,
      embedInputKeyTracker: `embed-src-${this.activeEmbedSlide?.id}-${Date.now()}`,
      isUserEditing: false,
      embedSlideKnowledgeBaseUrl: 'https://help.ahaslides.com/portal/en/kb/articles/using-the-embed-slide',
    }
  },
  computed: {
    ...mapGetters('embedSlide', ['activeEmbedSlide', 'embedSrcError']),
    ...mapState({
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
    }),
    showOnAudienceTooltip() {
      return this.$i18nPresenter.t('Please note that your audience will need to interact with the embedded content themselves')
    },
    showIframeOnParticipantScreen() {
      return this.activeEmbedSlide?.attributes?.showIframeOnParticipantScreen
    },
    embedSrc() {
      return this.activeEmbedSlide?.attributes?.embedSrc
    },
    embedInputKey() {
      return this.embedInputKeyTracker
    },
  },
  watch: {
    embedSrc() {
      // Only update the key if the change didn't come from the user editing
      if (!this.isUserEditing) {
        this.embedInputKeyTracker = `embed-src-${this.activeEmbedSlide?.id}-${Date.now()}`
      }
      this.isUserEditing = false
    },
  },
  mounted() {

  },
  methods: {
    ...mapActions('embedSlide', ['updateEmbedSlideAction', 'setEmbedSrcErrorAction']),
    ...mapActions(['setSyncLoadingAction', 'updateSlideActiveAction']),

    updateEmbedLink(data) {
      this.isUserEditing = true

      const inputValue = data.value.trim()
      const { sanitizedInputValue, isValid } = sanitizeEmbedSrc(inputValue)
      if (!isValid) {
        this.setEmbedSrcErrorAction(this.$i18nPresenter.t('Please enter an embeddable URL or embed code'))
        return
      }

      this.updateEmbedSlideAction({
        slideId: this.activeEmbedSlide.id,
        attributes: {
          embedSrc: sanitizedInputValue,
        },
      })
    },

    // slide title still update to Slide model, not update to EmbedSlide model
    updateSlideTitle(data) {
      this.updateSlide({
        title: data.value,
        titleHTML: data.value,
      })
    },

    updateSettingShowIframeOnParticipantScreen(e) {
      this.updateEmbedSlideAction({
        slideId: this.activeEmbedSlide.id,
        attributes: {
          showIframeOnParticipantScreen: e.target.checked,
        },
      })
    },

    updateSlide(payload) {
      this.setSyncLoadingAction(1)
      this.updateSlideActiveAction(payload)
      payload.presentationId = this.$store.state.presentation.id
      payload.accessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      const slideId = JSON.parse(
        JSON.stringify(this.$store.state.slideActive.id),
      )
      payload.presenting = this.$store.getters.isPresenting || this.$store.getters.isBackstage
      this.$http
        .put(Config.api.updateSlide + this.$store.state.slideActive.id, payload)
        .then(
          () => {
            const index = this.$store.state.presentation.slides.findIndex(
              (item) => {
                return item.id === slideId
              },
            )

            if (index === -1) return
            if (payload.title === '') {
              payload.title = ' '
            }
            if (payload.title) {
              // this.$store.state.presentation.slides[index].title =
              //   payload.title
              Util.mixpanelTracking(
                'Slide',
                'Update embed slide caption',
                'Done Update slide image caption',
              )
            }
            this.setSyncLoadingAction(2)
          },
          () => {
            this.setSyncLoadingAction(3)
          },
        )
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.select-group {
  label {
    cursor: pointer;
  }

  input {
    cursor: pointer;
  }
}

.learn-more-link {
  color: inherit;
  text-decoration: underline;
}
</style>
